import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import { SavedFiltersProps, useFiltersV2Select } from "PFApp/components/filters_v2";
import { SavedFiltersCollectionBadge } from "PFApp/components/filters_v2/parts/saved_filters_collection/parts/saved_filters_collection_badge";
import { Accordion, AccordionState } from "PFComponents/accordion";
import { LoadingDots } from "PFComponents/loading_dots";
import useStorage from "PFCore/helpers/use_storage";
import { useSavedFiltersV2 } from "PFCore/hooks/queries/filters/use_saved_filters_v2";
import { SavedFiltersV2 } from "PFTypes/saved_filters";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { isSavedFiltersCollectionSelected } from "../helpers";
import { SavedFiltersCollectionDeleteModal } from "./modals";
import { SavedFiltersCollectionShareModal } from "./modals/saved_filters_collection_share_modal";
import css from "./saved_filters_collection_badges.module.scss";

type FilterAction = {
  action: "delete" | "share";
} & Pick<SavedFiltersV2, "id" | "name">;

export const SavedFiltersCollectionBadges = ({ omit }: SavedFiltersProps) => {
  const { t } = useTranslation("filters", { keyPrefix: "savedFilters" });

  const { buffer: filtersMeta, collection } = useFiltersV2Select("buffer", "collection");

  const storageKey = `saved-filters-accordion-state-${collection}`;
  const [accordionState, setAccordionState] = useStorage<AccordionState>(storageKey, "open");

  const [filterAction, setFilterAction] = useState<FilterAction | null>(null);

  const { data: savedCollections, isLoading: isLoadingSavedCollections } = useSavedFiltersV2(collection!);

  const isSelected = useMemo(() => {
    const currentCollection = find(savedCollections, ["id", filterAction?.id]);

    return isSavedFiltersCollectionSelected(filtersMeta, currentCollection?.collection, omit);
  }, [savedCollections, filtersMeta, filterAction?.id, omit]);

  const handleStateChange = useCallback(
    (state: AccordionState) => {
      setAccordionState(state);
    },
    [setAccordionState]
  );

  const handleShareClick = useCallback((id: SavedFiltersV2["id"], name: string) => {
    setFilterAction({ action: "share", id, name });
  }, []);

  const handleDeleteClick = useCallback((id: SavedFiltersV2["id"], name: string) => {
    setFilterAction({ action: "delete", id, name });
  }, []);

  const handleModalClose = useCallback(() => {
    setFilterAction(null);
  }, []);

  if (isEmpty(savedCollections)) {
    return null;
  }

  const headerText = t("header", { count: savedCollections?.length || 0 });

  return (
    <div className={css.root}>
      <Accordion
        header={headerText}
        state={accordionState}
        onStateChange={handleStateChange}
        headerVariant="h5"
        iconSet="chevron"
        animated={true}
      >
        <div className={css.listContainer}>
          <div className={css.viewsBadges}>
            {(savedCollections || []).map((savedFilters) => (
              <SavedFiltersCollectionBadge
                savedFilters={savedFilters}
                key={savedFilters.id}
                onShare={handleShareClick}
                onDelete={handleDeleteClick}
                omit={omit}
              />
            ))}
            {isLoadingSavedCollections && <LoadingDots circlesEnabled circleSize="xs" />}
          </div>
        </div>
      </Accordion>
      {filterAction?.action === "share" && (
        <SavedFiltersCollectionShareModal
          onClose={handleModalClose}
          id={filterAction.id}
          name={filterAction.name}
        />
      )}

      {filterAction?.action === "delete" && (
        <SavedFiltersCollectionDeleteModal
          onClose={handleModalClose}
          id={filterAction.id}
          name={filterAction.name}
          isSelected={isSelected}
        />
      )}
    </div>
  );
};
